import React, { useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { PlusIcon, Search } from "lucide-react";
import Modal from "../components/Modal";
import useCustomerManagementStore from "../stores/customerManagementStore";

const CustomerManagement = () => {
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    nameOfOrganization: "",
    CustomerEmail: "",
    mobile: "",
  });

  const {
    customerData,
    createCustomer,
    updateCustomer,
    fetchCustomers,
    searchCustomer,
    isLoading,
    deleteCustomer,
  } = useCustomerManagementStore();

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    console.log("customerData updated:", customerData); // Log on state change
  }, [customerData]);

  const handleSearch = async (e) => {
    e.preventDefault();

    const value = e.target.value;
    setSearchTerm(value);

    const query = {
      nameOfOrganization: value,
    };

    if (!value) return fetchCustomers();
    await searchCustomer(query);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing) {
      await updateCustomer(selectedCustomerId, formData);
    } else {
      await createCustomer(formData);
    }
    fetchCustomers();
    setOpen(false);
    resetForm();
  };

  const handleDelete = async (customerId) => {
    if (!customerId) return;
    await deleteCustomer(customerId);
    fetchCustomers();
    setOpen(false);
  };

  const handleEdit = (customer) => {
    setFormData({
      nameOfOrganization: customer.nameOfOrganization,
      CustomerEmail: customer.CustomerEmail,
      mobile: customer.mobile,
    });
    setSelectedCustomerId(customer._id);
    setIsEditing(true);
    setOpen(true);
  };

  const resetForm = () => {
    setFormData({ nameOfOrganization: "", CustomerEmail: "", mobile: "" });
    setIsEditing(false);
    setSelectedCustomerId(null);
  };

  const columnDefs = [
    {
      headerName: "Customer / Organization Name",
      field: "nameOfOrganization",
      flex: 1,
    },
    { headerName: "Customer Email", field: "CustomerEmail", flex: 1 },
    { headerName: "Mobile", field: "mobile", flex: 1 },
    {
      headerName: "Action",
      field: "action",
      flex: 0.5,
      cellRenderer: (params) => (
        <div className="flex space-x-2">
          <button
            className="text-blue-500 hover:text-blue-700"
            onClick={() => handleEdit(params.data)}
          >
            <FiEdit size={18} />
          </button>
          <button
            className="text-red-500 hover:text-red-700"
            onClick={() => handleDelete(params.data._id)}
          >
            <RiDeleteBin6Line size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 mt-10 w-full">
      <h1 className="text-2xl font-bold mb-4">CUSTOMER MANAGEMENT</h1>
      <div className="mt-4 mb-4 flex justify-between items-center">
        <button
          className="flex text-md items-center gap-2 bg-[#a9e5f7] text-[#074659] font-bold px-4 py-2 rounded-full"
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
        >
          <PlusIcon size={30} /> CREATE CUSTOMER / ORGANIZATION
        </button>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={20} className="text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search By Customer / Organization Name"
            className="pl-10 pr-20 py-2 w-full border border-[#bcc9db] placeholder:text-[#6f7987] placeholder:text-sm placeholder:font-semibold rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
      <div className="ag-theme-alpine w-full h-[500px]">
        <AgGridReact
          rowData={customerData}
          rowHeight={45}
          headerHeight={50}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50, 100]} // Include 10 in the selector
        />
      </div>

      <Modal isOpen={open}>
        <form onSubmit={handleSubmit}>
          <div className="p-6 w-[1000px]">
            <h1 className="font-bold text-lg">
              {isEditing
                ? "EDIT CUSTOMER / ORGANIZATION"
                : "CREATE CUSTOMER / ORGANIZATION"}
            </h1>
            <hr className="w-full border border-[#bcc9db] mt-2" />
            <div className="grid grid-cols-3 gap-4">
              {Object.keys(formData).map((key) => (
                <div key={key} className="mt-5 flex flex-col">
                  <label
                    htmlFor={key}
                    className="font-semibold text-[#949494] text-sm"
                  >
                    {key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                  </label>
                  <input
                    type={key === "CustomerEmail" ? "email" : "text"}
                    id={key}
                    value={formData[key]}
                    onChange={handleChange}
                    placeholder={`Enter ${key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}`}
                    className="w-full border border-[#bcc9db] rounded-sm px-2 py-1 mt-1"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-3 bg-[#f5fdff] text-[#ffffff] mt-10 py-3 px-3">
            <button
              className="bg-[#7e90ab] px-5 py-1 rounded-full"
              onClick={() => setOpen(false)}
            >
              CANCEL
            </button>
            <button
              className="bg-[#063269] px-5 py-1 rounded-full"
              type="submit"
            >
              {isEditing ? "UPDATE" : "SUBMIT"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CustomerManagement;
